import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ArrowRight, Loader2, MapPin, Mail, Globe, Building2, User } from "lucide-react";
import { PageIntro } from "../components/PageIntro";
import { Reveal } from "../components/Motion";
import { PILLARS } from "../data";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FIELD =
  "w-full bg-white border border-mm-line rounded-md px-4 py-3 text-mm-ink placeholder:text-mm-muted/60 focus:border-mm-accent focus:outline-none focus:ring-2 focus:ring-mm-accent/20 transition-colors";
const LABEL = "block mb-2 text-sm font-medium text-mm-ink";

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", organisation: "", interest: "", message: "" });
  const [loading, setLoading] = useState(false);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      toast.error("Please complete name, email and message.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success("Enquiry received. Our team will be in touch shortly.");
      setForm({ name: "", email: "", organisation: "", interest: "", message: "" });
    } catch (err) {
      toast.error(err?.response?.data?.detail?.[0]?.msg || "Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="contact-page">
      <PageIntro
        label="Contact"
        title="Let's talk critical minerals."
        subtitle="Whether you represent capital, a project or a sovereign partner, we would welcome the conversation."
      />

      <section className="max-w-[1400px] mx-auto px-6 md:px-10 py-24 md:py-28">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
          {/* Info */}
          <div className="lg:col-span-4">
            <Reveal className="space-y-6">
              {[
                { icon: MapPin, k: "Registered Office", v: "128 City Road, London EC1V 2NX, United Kingdom" },
                { icon: User, k: "Corporate Contact", v: "Mr. Pradeep Venkataramakrishna — Founder & Managing Director" },
                { icon: Mail, k: "Email", v: "info@marsminingltd.com", href: "mailto:info@marsminingltd.com" },
                { icon: Globe, k: "Website", v: "www.marsminingltd.com", href: "https://www.marsminingltd.com" },
                { icon: Building2, k: "Company Reg. No.", v: "17132212" },
              ].map((it) => (
                <div key={it.k} className="flex items-start gap-4">
                  <div className="h-11 w-11 rounded-md bg-mm-light flex items-center justify-center shrink-0">
                    <it.icon size={19} className="text-mm-blue" />
                  </div>
                  <div>
                    <p className="font-mono text-xs tracking-[0.16em] uppercase text-mm-muted">{it.k}</p>
                    {it.href ? (
                      <a href={it.href} target={it.href.startsWith("http") ? "_blank" : undefined} rel="noreferrer" data-testid={`contact-info-${it.k.toLowerCase().replace(/[^a-z]+/g, "-")}`} className="text-mm-ink font-medium hover:text-mm-blue transition-colors break-words">{it.v}</a>
                    ) : (
                      <p className="text-mm-ink font-medium">{it.v}</p>
                    )}
                  </div>
                </div>
              ))}
              <div className="pt-4 border-t border-mm-line">
                <p className="font-mono text-xs tracking-[0.16em] uppercase text-mm-muted mb-3">Areas of interest</p>
                <div className="flex flex-wrap gap-2">
                  {PILLARS.map((p) => (
                    <span key={p} className="text-xs text-mm-muted border border-mm-line rounded px-3 py-1.5">{p}</span>
                  ))}
                </div>
              </div>
            </Reveal>
          </div>

          {/* Form */}
          <div className="lg:col-span-8">
            <Reveal>
              <form onSubmit={submit} data-testid="contact-form" className="bg-white border border-mm-line rounded-lg p-7 md:p-9 shadow-card space-y-5">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div>
                    <label className={LABEL}>Name *</label>
                    <input data-testid="contact-name" className={FIELD} value={form.name} onChange={update("name")} placeholder="Full name" />
                  </div>
                  <div>
                    <label className={LABEL}>Email *</label>
                    <input data-testid="contact-email" type="email" className={FIELD} value={form.email} onChange={update("email")} placeholder="you@company.com" />
                  </div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div>
                    <label className={LABEL}>Organisation</label>
                    <input data-testid="contact-org" className={FIELD} value={form.organisation} onChange={update("organisation")} placeholder="Company / fund" />
                  </div>
                  <div>
                    <label className={LABEL}>Area of interest</label>
                    <select data-testid="contact-interest" className={FIELD} value={form.interest} onChange={update("interest")}>
                      <option value="">Select an area</option>
                      {PILLARS.map((p) => <option key={p} value={p}>{p}</option>)}
                    </select>
                  </div>
                </div>
                <div>
                  <label className={LABEL}>Message *</label>
                  <textarea data-testid="contact-message" rows={6} className={`${FIELD} resize-none`} value={form.message} onChange={update("message")} placeholder="Tell us about your project, capital or partnership interest." />
                </div>
                <button
                  type="submit"
                  data-testid="contact-submit"
                  disabled={loading}
                  className="inline-flex items-center gap-2 px-8 py-3.5 bg-mm-blue text-white font-semibold rounded-md hover:bg-mm-navy transition-colors disabled:opacity-60"
                >
                  {loading ? <Loader2 size={18} className="animate-spin" /> : <ArrowRight size={18} />}
                  {loading ? "Sending…" : "Send enquiry"}
                </button>
              </form>
            </Reveal>
          </div>
        </div>
      </section>
    </div>
  );
}
